import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { useCan } from '../../lib/rbac';
import { useUIStore } from '../../stores/uiStore';
import { usePagination } from '../../hooks/usePagination';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Pagination } from '../../components/shared/Pagination';
import { Search, Loader2, X, Package, ExternalLink } from 'lucide-react';

const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  delivered: 'ok', shipped: 'warning', ordered: 'info', cancelled: 'error',
};

function statusType(s: string): 'ok' | 'warning' | 'error' | 'info' | 'unknown' {
  const lower = (s || '').toLowerCase();
  for (const [k, v] of Object.entries(STATUS_MAP)) if (lower.includes(k)) return v;
  return 'unknown';
}

export function ReportOrdersTab() {
  const canMoney = useCan()('global.view_money');
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('amazon-report-orders');
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ seller_id: String(sellerId), page: String(pag.page), per_page: String(pag.perPage) });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (search) params.set('search', search);
    const res = await api.get<any>(`/amazon/reports/orders?${params}`);
    if (res.success) { setData(res.data || []); pag.setTotal((res as any).meta?.total || 0); }
    setLoading(false);
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (orderId: string) => {
    setDetailLoading(true);
    const res = await api.get<any>(`/amazon/reports/orders/${orderId}?seller_id=${sellerId}`);
    if (res.success && res.data) setDetail(res.data);
    setDetailLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder="Order ID, ASIN, buyer..."
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]">
          <option value="all">All Statuses</option>
          <option value="ordered">Ordered</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="text-xs text-[var(--color-text-tertiary)]">{pag.total} orders</span>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
              <th className="text-left px-3 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Amazon Order</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Date</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Status</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Buyer</th>
              <th className="text-center px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Items</th>
              <th className="text-right px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Net Total</th>
              <th className="text-center px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Linked</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" /></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-16 text-[var(--color-text-tertiary)]">No orders found</td></tr>
            ) : data.map((row) => (
              <tr key={row.id} onClick={() => openDetail(row.amazon_order_id)}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors">
                <td className="px-3 py-3 font-mono text-xs text-[var(--color-text)]">{row.amazon_order_id}</td>
                <td className="px-2 py-3 text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
                  {row.order_date ? new Date(row.order_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                </td>
                <td className="px-2 py-3"><StatusBadge status={statusType(row.order_status)} label={row.order_status} /></td>
                <td className="px-2 py-3 text-xs text-[var(--color-text)] truncate max-w-[150px]">{row.buyer_name || '—'}</td>
                <td className="px-2 py-3 text-center text-xs text-[var(--color-text)]">{row.item_count || 0}</td>
                <td className="px-2 py-3 text-right font-mono text-xs text-[var(--color-text)]">{canMoney ? `$${Number(row.net_total || 0).toFixed(2)}` : '\u2014'}</td>
                <td className="px-2 py-3 text-center">
                  {row.purchase_id ? (
                    <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-[10px] font-medium">Linked</span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded text-[10px]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Detail Modal */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
              <h3 className="font-bold text-[var(--color-text)]">Order Detail</h3>
              <button onClick={() => setDetail(null)} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
            </div>
            {detailLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>
            ) : detail && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-xs text-[var(--color-text-tertiary)]">Amazon Order</span><p className="font-mono text-[var(--color-text)]">{detail.amazon_order_id}</p></div>
                  <div><span className="text-xs text-[var(--color-text-tertiary)]">Date</span><p className="text-[var(--color-text)]">{detail.order_date ? new Date(detail.order_date).toLocaleDateString() : '—'}</p></div>
                  <div><span className="text-xs text-[var(--color-text-tertiary)]">Status</span><p><StatusBadge status={statusType(detail.order_status)} label={detail.order_status} /></p></div>
                  <div><span className="text-xs text-[var(--color-text-tertiary)]">Buyer</span><p className="text-[var(--color-text)]">{detail.buyer_name || '—'}</p></div>
                  {canMoney && <div><span className="text-xs text-[var(--color-text-tertiary)]">Net Total</span><p className="font-bold text-[var(--color-text)]">${Number(detail.net_total || 0).toFixed(2)}</p></div>}
                  <div><span className="text-xs text-[var(--color-text-tertiary)]">PO Number</span><p className="text-[var(--color-text)]">{detail.purchase_order_number || '—'}</p></div>
                </div>

                {/* Line Items */}
                {detail.line_items?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-[var(--color-text-secondary)] uppercase mb-2">Line Items</h4>
                    <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)]">
                          <th className="text-left p-2">ASIN</th><th className="text-left p-2">Title</th>
                          <th className="text-center p-2">Qty</th><th className="text-right p-2">Price</th><th className="text-right p-2">Net</th>
                        </tr></thead>
                        <tbody>{detail.line_items.map((li: any, i: number) => (
                          <tr key={i} className="border-t border-[var(--color-border)]">
                            <td className="p-2 font-mono text-teal-500">{li.asin}</td>
                            <td className="p-2 text-[var(--color-text)] truncate max-w-[200px]">{li.title}</td>
                            <td className="p-2 text-center">{li.quantity}</td>
                            <td className="p-2 text-right font-mono">{canMoney ? `$${Number(li.unit_price || 0).toFixed(2)}` : '\u2014'}</td>
                            <td className="p-2 text-right font-mono">{canMoney ? `$${Number(li.net_total || 0).toFixed(2)}` : '\u2014'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Shipments */}
                {detail.shipments?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-[var(--color-text-secondary)] uppercase mb-2">Shipments</h4>
                    {detail.shipments.map((s: any, i: number) => (
                      <div key={i} className="bg-[var(--color-bg-tertiary)] rounded-lg p-3 mb-2">
                        <div className="flex justify-between items-center">
                          <span className="font-mono text-xs text-[var(--color-text)]">{s.shipment_id}</span>
                          <StatusBadge status={statusType(s.shipment_status || s.delivery_status)} label={s.delivery_status || s.shipment_status} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-[var(--color-text-secondary)]">
                          {s.ship_to_city && <span>📍 {s.ship_to_city}, {s.ship_to_state}</span>}
                          {s.expected_delivery_date && <span>📅 ETA: {new Date(s.expected_delivery_date).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
